import { Link } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { Home, LayoutDashboard } from 'lucide-react';
import { useState, useEffect } from 'react';
import { usePageTitle } from '@hooks/usePageTitle';
import ContourBG from '@components/ContourBG';

const NotFound = () => {
  usePageTitle('Page not found — Castle Atlas');
  const { user } = useAuth();
  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const obs = new MutationObserver(() => setIsDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const dashColor = isDark ? '#57534e' : '#d4cdc7';
  const mutedColor = isDark ? '#78716c' : '#a8a29e';

  return (
    <div id="main-content" tabIndex={-1} style={{
      minHeight: '100vh', position: 'relative',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 24px',
      backgroundColor: isDark ? '#0e0d0c' : '#faf9f7',
      transition: 'background-color 0.3s ease',
    }}>
      {/* Contour header — same treatment as Dashboard / Settings */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 180, overflow: 'hidden', pointerEvents: 'none' }}>
        <ContourBG isDark={isDark} size="compact" opacity={0.9} />
      </div>

      <div style={{ textAlign: 'center', maxWidth: 420, animation: 'fade-up 0.4s ease-out both', position: 'relative', zIndex: 1 }}>
        <style>{`@keyframes fade-up{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}`}</style>

        {/* Plate eyebrow */}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: '#ea580c', letterSpacing: '0.14em' }}>OFF THE MAP</span>
          <span style={{ width: 22, height: 1, background: dashColor, display: 'inline-block' }} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: mutedColor, letterSpacing: '0.14em', textTransform: 'uppercase' }}>404</span>
        </div>

        <div style={{ fontSize: 80, fontWeight: 900, color: isDark ? '#292524' : '#e8e4df', marginBottom: 16, letterSpacing: '-0.04em', lineHeight: 1, fontFamily: 'var(--font-display)' }}>404</div>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: isDark ? '#fafaf9' : '#1c1917', marginBottom: 10, fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}>Page not found</h1>
        <p style={{ color: isDark ? '#78716c' : '#a8a29e', marginBottom: 32, lineHeight: 1.6, fontSize: 14 }}>
          This territory isn't on the map. It may have moved, or the URL might be wrong.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          {user ? (
            <Link to="/dashboard" className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <LayoutDashboard size={16} strokeWidth={2.5} />
              Go to Dashboard
            </Link>
          ) : (
            <Link to="/" className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Home size={16} strokeWidth={2.5} />
              Back to home
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotFound;
